import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import {
    AbstractControl,
    ReactiveFormsModule,
    UntypedFormControl,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';
import { isArray, omit } from 'lodash-es';
import { FormSelectOption } from '../../../entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';
import { MultiFormSelectOptions } from '../../../entities/multi-form-select-options.entity';
import { IsControlRequired } from '../../../helpers/control-required.helper';
import { ShBaseFormComponent } from '../../_core/base-form/base-form.component';
import { ShFormTextBaseComponent } from '../base-simple/form-text-base.component';
import { ShFormTextComplexBaseComponent } from '../base-complex/form-text-complex-base.component';
import { AsyncPipe, NgClass } from '@angular/common';
import { ShFormGroupComponent } from '../../_core/form-group/form-group.component';

@Component({
    selector: 'sh-form-text-input',
    templateUrl: './form-text.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
    ShFormGroupComponent,
    ShFormTextComplexBaseComponent,
    ReactiveFormsModule,
    NgClass,
    ShFormTextBaseComponent,
    AsyncPipe
]
})
export class ShFormTextInputComponent extends ShBaseFormComponent implements OnDestroy, OnInit {
    public innerErrorMessage$: BehaviorSubject<string> = new BehaviorSubject('');

    @Input() autocomplete?: string;
    @Input() placeholder?: string;
    @Input() autocompleteFct?: (term: string) => Observable<MultiFormSelectOptions>;
    @Input() autocompleteDebounce: number = 300;
    @Input() autocompleteLoadingMessage: string;
    @Input() autocompleteNoResultsMessage: string;
    @Input() autocompleteLockOnInvalid: boolean;
    @Input() maxLength?: number;
    @Input() autoFocus: boolean = false;
    @Output() selectedSuggestions: EventEmitter<FormSelectOption<string>[]> = new EventEmitter<
        FormSelectOption<string>[]
    >();

    public ngOnInit(): void {
        super.ngOnInit();

        this.control.addValidators([this.noWhitespaceValidator]);
        this.control.updateValueAndValidity();

        this.control.valueChanges.subscribe(_ => {
            if (this.control.getError('whitespace')) {
                this.innerErrorMessage$.next(this.translateService.instant('form.error.required-field'));
            } else {
                this.innerErrorMessage$.next('');
            }
        });
    }

    public onInternalError(error: string): void {
        this.innerErrorMessage$.next(error);
        if (error) {
            this.control.setErrors({ ...this.control.errors, internal: '' });
        } else {
            const newErrors: ValidationErrors = omit(this.control.errors, 'internal');
            this.control.setErrors(Object.keys(newErrors).length > 0 ? newErrors : null);
        }
    }

    public get noWhitespaceValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const isRequired: boolean = IsControlRequired(control as UntypedFormControl);
            let isIndeterminate: boolean = false;
            const controlValue: any = control.value;

            if (isArray(controlValue)) {
                return null;
            }

            let value: string;

            if (controlValue instanceof FormValue) {
                value = controlValue.state === FormValueState.SET ? controlValue.value : '';
                isIndeterminate = controlValue.state === FormValueState.INDETERMINATE;
            } else {
                value = controlValue ?? '';
            }

            return isRequired && !isIndeterminate && (value ?? '').trim().length === 0 ? { whitespace: true } : null;
        };
    }

    public suggestionsSelected(suggestions: FormSelectOption<string>[]): void {
        this.selectedSuggestions.emit(suggestions);
    }

    public ngOnDestroy(): void {
        this.innerErrorMessage$.complete();
        super.ngOnDestroy();
    }
}
